import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skip } from 'rxjs';
import { PageLayout } from '../../PageLayout';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { AttachmentTable } from '../../Compounds/AttachmentTable';
import { CardButton } from '../../Compounds/Buttons/CardButton';
import { DrawerActions } from '../../Compounds/DetailsDrawer/DrawerActions';
import { RecordUnavailable } from '../../Compounds/RecordUnavailable/RecordUnavailable';
import {
  useWorkflowOrders,
  getWorkflowOrdersSubject,
  getWorkflowOrdersLoadingSubject
} from '../../../store/WorkflowOrder';
import { getErrorByDomainSubject } from '../../../store/Error';
import { postQuoteApproval, WorkflowOrder } from '../../../Api/Orders/ordersApi';
import {
  getPreferencesByKey,
  getSelectedAccountIdsForUrl,
  getSelectedAccountsSubject,
  getUser,
  isQuoteApprovalEnabled,
  OrderRequestPreferences,
  shouldSeeComments
} from '../../../store/User';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { QuoteApprovalSection } from './QuoteApprovalSection';
import { orderRequestDetailFields } from './orderRequestDetailFields';
import { findOrder, inAccountScope, orderFilesParentId, orderThreadId } from './orderKeys';

// A pair's narrowest fit: its 160px label, the 24px gap and room for a short value.
const DETAIL_PAIRS_GRID = 'grid grid-cols-[repeat(auto-fill,minmax(312px,1fr))] gap-x-6 gap-y-8 items-baseline';
// Attachments and comments keep a fixed column beside Details from xl up, and stack below it under that.
const SIDE_COLUMN = 'xl:w-[442px] xl:shrink-0';

export const WorkflowOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchWorkflowOrders } = useWorkflowOrders();
  const fetchRef = useCallback(fetchWorkflowOrders, []);
  const { setSnack } = useSnackbar();
  const [listedOrder, setListedOrder] = useState<WorkflowOrder | undefined>();
  const [loading, setLoading] = useState(true);
  const [accountScope, setAccountScope] = useState<string[]>(getSelectedAccountIdsForUrl());
  const order = listedOrder && inAccountScope(listedOrder, accountScope) ? listedOrder : undefined;

  const user = getUser();
  const orderRequestPrefs = getPreferencesByKey('order_request') as OrderRequestPreferences | undefined;
  const approvalEnabled = isQuoteApprovalEnabled(orderRequestPrefs, user);
  // An empty list plus an orders-domain error is a failed load, not a request that is not there.
  const listLoadFailed =
    !getWorkflowOrdersSubject().getValue().length && !!getErrorByDomainSubject().value['workflow-orders'];
  // The account scope lands after the first render, and the list cannot be asked for without it.
  const scopePending = !accountScope.length && !!user?.accounts?.length;
  const showApproval =
    approvalEnabled && order?.workflow_tab === 'reviewing_approving_quotes' && order?.source_type === 'opportunity';

  const pageActions = (
    <div className="flex items-center gap-x-2">
      {order && (
        <DrawerActions
          componentName="workflow-order"
          data={order}
          setShow={() => {}}
        />
      )}
      <CardButton
        text="Back"
        onClick={() => navigate(-1)}
        className="h-8 px-2 pl-4 ml-auto border border-grey-500 rounded-lg shadow"
      />
    </div>
  );

  useEffect(() => {
    // Only a read that starts after this page opened is fresh; the loading flag replayed on subscribe may be another scope's.
    let refreshed = false;
    const loadingSub = getWorkflowOrdersLoadingSubject().subscribe(setLoading);
    const freshReadSub = getWorkflowOrdersLoadingSubject()
      .pipe(skip(1))
      .subscribe((isLoading) => {
        if (isLoading) refreshed = true;
      });
    const ordersSub = getWorkflowOrdersSubject().subscribe((orders) => {
      const found = findOrder(orders, id);
      setListedOrder(found);
      if (!found && orders.length && !refreshed) {
        refreshed = true;
        fetchRef(true);
      }
    });
    // The accounts subject replays on subscribe, so this covers the cold load an empty scope would skip.
    const accountsSub = getSelectedAccountsSubject().subscribe(() => {
      setAccountScope(getSelectedAccountIdsForUrl());
      fetchRef();
    });
    return () => {
      loadingSub.unsubscribe();
      freshReadSub.unsubscribe();
      ordersSub.unsubscribe();
      accountsSub.unsubscribe();
    };
  }, [id, fetchRef]);

  const handleApprove = async (quoteIds: string[]) => {
    if (!order) return false;
    const { error } = await postQuoteApproval(order.sf_id, quoteIds, order.name || undefined, order.account_id);
    if (error) {
      setSnack({ message: error.message || 'Failed to approve quotes', type: 'error', open: true });
      return false;
    }
    setSnack({ message: 'Quotes approved successfully', type: 'success', open: true });
    fetchWorkflowOrders(true);
    return true;
  };

  if (!loading && !scopePending && !order) {
    // The notice carries the only way out, so the header's Back is left off here.
    return (
      <PageLayout pageTitle="Order Request Details">
        <RecordUnavailable
          message={
            listLoadFailed
              ? 'The order requests list could not be loaded, so this request cannot be shown.'
              : 'This order request is not available for this account. It may have moved on to a project or been removed.'
          }
          actionText="Back to Orders"
          onAction={() => navigate('/orders')}
          dataTestId="order-unavailable"
        />
      </PageLayout>
    );
  }

  if (!order) {
    return (
      <PageLayout
        pageTitle="Order Request Details"
        pageActions={pageActions}>
        <ComponentSpinner />
      </PageLayout>
    );
  }

  const detailFields = orderRequestDetailFields(order);
  const threadId = orderThreadId(order);

  return (
    <PageLayout
      pageTitle="Order Request Details"
      pageActions={pageActions}>
      <div className="xl:flex xl:gap-4">
        <div className="min-w-0 xl:flex-1">
          <DetailViewCard
            collapsed={false}
            enableCollapse
            title="Details">
            <div className="px-6 py-5">
              {/* Pairs per row follow the card's own width, which the side navigation changes as well as the viewport. */}
              <div className={DETAIL_PAIRS_GRID}>
                {detailFields.map((field) => (
                  <div
                    key={field.title}
                    className="grid grid-cols-[160px_minmax(0,1fr)] gap-x-6 items-baseline">
                    <span className="text-sm font-bold text-gray-600">{field.title}</span>
                    <span className="text-sm text-gray-900 break-words">{field.value || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          </DetailViewCard>
        </div>
        <div className={SIDE_COLUMN}>
          <AttachmentTable
            id={orderFilesParentId(order)}
            parentType="Opportunity"
            readOnly
          />
          {shouldSeeComments() && threadId && (
            <DetailsComments
              id={threadId}
              entityType="Opportunity"
              entityAccountId={order.account_id || ''}
            />
          )}
        </div>
      </div>

      {showApproval && (
        <div className="mt-4">
          <QuoteApprovalSection
            opportunitySfId={order.sf_id}
            accountId={order.account_id}
            onApprove={handleApprove}
          />
        </div>
      )}
    </PageLayout>
  );
};
